import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, AuditLogEntry } from '@nexus/shared/types';

// =============================================================================
// Notification Store — transforms audit log entries into notifications
// with localStorage-based read tracking and 30s polling support.
// =============================================================================

export interface Notification {
  id: string;
  title: string;
  description: string;
  type: 'create' | 'update' | 'delete' | 'execute' | 'system';
  entityType: string;
  timestamp: string;
  read: boolean;
}

interface NotificationState {
  items: Notification[];
  isLoading: boolean;
  unreadCount: number;
  fetch: () => Promise<void>;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

// ---------------------------------------------------------------------------
// localStorage helpers for read-state persistence
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'nexus_read_notifications';
const MAX_READ_IDS = 200;

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>) {
  const arr = Array.from(ids).slice(-MAX_READ_IDS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
}

// ---------------------------------------------------------------------------
// Audit entry → Notification mapping
// ---------------------------------------------------------------------------
const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  execute: 'Executed',
};

function auditToNotification(
  raw: Record<string, unknown>,
  readIds: Set<string>,
): Notification {
  // Normalize fields the same way auditStore does
  const entry: AuditLogEntry = {
    ...raw,
    createdAt: fromUnix(raw.createdAt as number | string),
    changes:
      typeof raw.changes === 'string'
        ? JSON.parse(raw.changes as string)
        : (raw.changes as Record<string, unknown> | undefined),
  } as AuditLogEntry;

  const actionLabel = ACTION_LABELS[entry.action] || entry.action;
  const entityLabel = entry.entityType?.replace(/_/g, ' ') || 'item';

  // Try to extract a summary from changes if available
  let description = `${actionLabel} a ${entityLabel}`;
  if (entry.changes && typeof entry.changes === 'object') {
    const summary = (entry.changes as Record<string, unknown>).summary;
    if (typeof summary === 'string' && summary.length > 0) {
      description = summary;
    }
  }

  return {
    id: entry.id,
    title: `${actionLabel} ${entityLabel}`,
    description,
    type: (ACTION_LABELS[entry.action] ? entry.action : 'system') as Notification['type'],
    entityType: entry.entityType || '',
    timestamp: entry.createdAt,
    read: readIds.has(entry.id),
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const useNotificationStore = create<NotificationState>((set, get) => ({
  items: [],
  isLoading: false,
  unreadCount: 0,

  fetch: async () => {
    set({ isLoading: true });
    try {
      const res = await api.get<ApiResponse<AuditLogEntry[]>>(
        `/api/audit${toQueryString({ perPage: '30' })}`,
      );
      const rawEntries = (res.data ?? []) as unknown as Record<string, unknown>[];
      const readIds = getReadIds();
      const items = rawEntries.map((e) => auditToNotification(e, readIds));
      const unreadCount = items.filter((n) => !n.read).length;
      set({ items, unreadCount, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  markRead: (id: string) => {
    const readIds = getReadIds();
    readIds.add(id);
    saveReadIds(readIds);
    set((s) => {
      const wasUnread = s.items.find((n) => n.id === id && !n.read);
      return {
        items: s.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: wasUnread ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
      };
    });
  },

  markAllRead: () => {
    const readIds = getReadIds();
    const { items } = get();
    items.forEach((n) => readIds.add(n.id));
    saveReadIds(readIds);
    set((s) => ({
      items: s.items.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },
}));
